import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const BASE = import.meta?.env?.BASE_URL ?? "/";

const NASCAR_RACE_LOGS_SOURCE = `${BASE}data/nascar/race-logs.json`;

const SERIES_CONFIG = {
  cup: {
    key: "cup",
    label: "Cup",
    title: "NASCAR Cup",
    route: "/nascar/cup/lineup-lab",
    projections: `${BASE}data/nascar/cup/latest/projections.json`,
    siteIds: `${BASE}data/nascar/cup/latest/site_ids.json`,
    driverData: `${BASE}data/nascar/cup/latest/data.json`,
    meta: `${BASE}data/nascar/cup/latest/meta.json`,
    numberLogoBase: `${BASE}logos/nascar/cup`,
  },
  xfinity: {
    key: "xfinity",
    label: "O'Reilly",
    title: "NASCAR O'Reilly",
    route: "/nascar/oreilly/lineup-lab",
    projections: `${BASE}data/nascar/xfinity/latest/projections.json`,
    siteIds: `${BASE}data/nascar/xfinity/latest/site_ids.json`,
    driverData: `${BASE}data/nascar/xfinity/latest/data.json`,
    meta: `${BASE}data/nascar/xfinity/latest/meta.json`,
    numberLogoBase: `${BASE}logos/nascar/xfinity`,
  },
  trucks: {
    key: "trucks",
    label: "Trucks",
    title: "NASCAR Trucks",
    route: "/nascar/trucks/lineup-lab",
    projections: `${BASE}data/nascar/trucks/latest/projections.json`,
    siteIds: `${BASE}data/nascar/trucks/latest/site_ids.json`,
    driverData: `${BASE}data/nascar/trucks/latest/data.json`,
    meta: `${BASE}data/nascar/trucks/latest/meta.json`,
    numberLogoBase: `${BASE}logos/nascar/trucks`,
  },
};

const SITE_CONFIG = {
  dk: {
    key: "dk",
    label: "DraftKings",
    short: "DK",
    logo: `${BASE}logos/dk.png`,
    cap: 50000,
    rosterSize: 6,
    salaryKeys: ["DK Sal", "DK Salary", "dk_sal", "dk salary"],
    projectionKeys: ["DK Proj", "DK Projection", "dk_proj", "Projection", "Proj"],
    ownershipKeys: ["DK pOWN%", "DK pOWN", "DK Own%", "DK Ownership"],
    optimalKeys: ["DK Opt%", "DK Opt", "DK Optimal%", "DK Optimal Rate"],
    floorKeys: ["DK Floor", "Floor"],
    ceilingKeys: ["DK Ceiling", "Ceiling"],
  },
  fd: {
    key: "fd",
    label: "FanDuel",
    short: "FD",
    logo: `${BASE}logos/fd.png`,
    cap: 50000,
    rosterSize: 5,
    salaryKeys: ["FD Sal", "FD Salary", "fd_sal", "fd salary"],
    projectionKeys: ["FD Proj", "FD Projection", "fd_proj", "Projection", "Proj"],
    ownershipKeys: ["FD pOWN%", "FD pOWN", "FD Own%", "FD Ownership"],
    optimalKeys: ["FD Opt%", "FD Opt", "FD Optimal%", "FD Optimal Rate"],
    floorKeys: ["FD Floor", "Floor"],
    ceilingKeys: ["FD Ceiling", "Ceiling"],
  },
};

const SORT_OPTIONS = [
  { key: "salary", label: "Salary" },
  { key: "proj", label: "Projection" },
  { key: "value", label: "Value" },
  { key: "ownership", label: "Ownership" },
  { key: "optimal", label: "Optimal" },
  { key: "start", label: "Start" },
  { key: "projFinish", label: "Proj Finish" },
  { key: "name", label: "Driver" },
  { key: "team", label: "Team" },
];

const FILE_ROWS_CACHE = new Map();
const FILE_REQUEST_CACHE = new Map();

function normText(value) {
  return String(value ?? "").trim();
}

function normKey(value) {
  return normText(value).toLowerCase().replace(/[%\s._()/-]/g, "");
}

function normName(value) {
  return normText(value)
    .toLowerCase()
    .replace(/[.’']/g, "")
    .replace(/,?\s+(jr|sr|ii|iii|iv|v)$/i, "")
    .replace(/[^a-z0-9]/g, "");
}

function numberLike(value) {
  if (value == null || value === "") return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const parsed = Number(String(value).replace(/[$,%\s,]/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
}

function asRate(value) {
  // NASCAR projection percentage fields are stored as percentage points:
  // 1.0 means 1.0%, 0.7 means 0.7%, and 52.4 means 52.4%.
  // Convert them to decimal rates once so fmtPct can display them correctly.
  return numberLike(value) / 100;
}

function firstValue(row, aliases, fallback = null) {
  if (!row) return fallback;
  const index = new Map(Object.keys(row).map((key) => [normKey(key), key]));
  for (const alias of aliases) {
    const actual = index.get(normKey(alias));
    if (actual != null && row[actual] != null && row[actual] !== "") return row[actual];
  }
  return fallback;
}

function fmtMoney(value) {
  return `$${Math.round(numberLike(value)).toLocaleString()}`;
}

function fmt1(value) {
  return Number.isFinite(Number(value)) ? Number(value).toFixed(1) : "—";
}

function fmtPct(value) {
  return Number.isFinite(Number(value)) ? `${(Number(value) * 100).toFixed(1)}%` : "—";
}

function escapeCSV(value) {
  const text = String(value ?? "");
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function seriesFromPath(pathname) {
  const path = String(pathname || "").toLowerCase();
  if (path.includes("/oreilly/") || path.includes("/xfinity/")) return "xfinity";
  if (path.includes("/trucks/")) return "trucks";
  return "cup";
}

function useStickyState(key, initialValue) {
  const readValue = React.useCallback(() => {
    try {
      const raw = window.localStorage.getItem(key);
      if (raw != null) return JSON.parse(raw);
    } catch (_) {}
    return typeof initialValue === "function" ? initialValue() : initialValue;
  }, [key, initialValue]);

  const [value, setValue] = useState(() => readValue());
  const keyRef = useRef(key);

  useEffect(() => {
    if (keyRef.current !== key) {
      keyRef.current = key;
      setValue(readValue());
    }
  }, [key, readValue]);

  useEffect(() => {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (_) {}
  }, [key, value]);

  return [value, setValue];
}

async function fetchRows(url, { fresh = false } = {}) {
  if (!fresh && FILE_ROWS_CACHE.has(url)) return FILE_ROWS_CACHE.get(url);
  if (!fresh && FILE_REQUEST_CACHE.has(url)) return FILE_REQUEST_CACHE.get(url);

  const separator = url.includes("?") ? "&" : "?";
  const requestUrl = fresh ? `${url}${separator}_=${Date.now()}` : url;
  const request = fetch(requestUrl, { cache: fresh ? "no-store" : "force-cache" })
    .then((response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}: ${url}`);
      return response.json();
    })
    .then((json) => {
      const rows = Array.isArray(json) ? json : json?.rows || json?.data || json?.players || [];
      FILE_ROWS_CACHE.set(url, rows);
      FILE_REQUEST_CACHE.delete(url);
      return rows;
    })
    .catch((error) => {
      FILE_REQUEST_CACHE.delete(url);
      throw error;
    });

  FILE_REQUEST_CACHE.set(url, request);
  return request;
}

async function fetchJsonFresh(url) {
  const separator = url.includes("?") ? "&" : "?";
  const response = await fetch(`${url}${separator}_=${Date.now()}`, { cache: "no-store" });
  if (!response.ok) throw new Error(`HTTP ${response.status}: ${url}`);
  return response.json();
}

function siteList(siteIds, siteKey) {
  if (Array.isArray(siteIds?.[siteKey])) return siteIds[siteKey];
  if (Array.isArray(siteIds?.sites?.[siteKey])) return siteIds.sites[siteKey];
  return [];
}

function buildSiteLookup(siteIds, siteKey) {
  const byName = new Map();
  for (const record of siteList(siteIds, siteKey)) {
    const candidates = [
      record?.name,
      record?.player,
      record?.Player,
      record?.Name,
      record?.display,
      record?.siteName,
      ...(Array.isArray(record?.aliases) ? record.aliases : []),
    ].filter(Boolean);
    for (const candidate of candidates) {
      const key = normName(candidate);
      if (key && !byName.has(key)) byName.set(key, record);
    }
  }
  return byName;
}

function getRecordId(record, siteKey) {
  if (!record) return null;
  return (
    record.id ??
    record.ID ??
    record.playerId ??
    record.player_id ??
    (siteKey === "dk" ? record.dk_id ?? record.DK_ID : record.fd_id ?? record.FD_ID) ??
    null
  );
}

function getRecordSalary(record) {
  return numberLike(record?.salary ?? record?.Salary ?? record?.sal ?? record?.SAL ?? 0);
}

function getDriverName(row) {
  return normText(firstValue(row, ["Driver", "Driver Name", "Name", "Player", "driver", "name"], ""));
}

function getTeamName(row) {
  return normText(
    firstValue(
      row,
      [
        "Team",
        "Team Name",
        "Organization",
        "Race Team",
        "Owner",
        "team",
        "team_name",
        "organization",
      ],
      ""
    )
  );
}

function getCarNumber(row) {
  return normText(firstValue(row, ["Car", "Car #", "Car Number", "Number", "No", "#"], ""));
}

function getMake(row) {
  return normText(firstValue(row, ["Make", "Manufacturer", "OEM"], ""));
}

function mergeDriverRows(projectionRows, driverRows) {
  const driverMap = new Map();
  for (const row of driverRows || []) {
    const name = getDriverName(row);
    if (name) driverMap.set(normName(name), row);
  }

  return (projectionRows || []).map((projection) => {
    const name = getDriverName(projection);
    const supplemental = driverMap.get(normName(name)) || {};
    return { ...supplemental, ...projection, __supplemental: supplemental };
  });
}

function driverNumberCandidates(seriesConfig, player) {
  const number = String(player?.carNumber || "").replace(/^#/, "").trim();
  if (!number) return [];
  return [
    `${seriesConfig.numberLogoBase}/${number}.png`,
    `${seriesConfig.numberLogoBase}/${number}.webp`,
    `${BASE}logos/nascar/${seriesConfig.key}/${number}.png`,
    `${BASE}logos/nascar/${seriesConfig.key}/${number}.webp`,
  ];
}

function DriverNumber({ player, seriesConfig, size = "md" }) {
  const candidates = useMemo(() => driverNumberCandidates(seriesConfig, player), [seriesConfig, player]);
  const [index, setIndex] = useState(0);
  useEffect(() => setIndex(0), [player?.id, seriesConfig.key]);

  const dimensions = size === "sm" ? "h-8 w-10" : "h-10 w-12";
  if (!candidates.length || index >= candidates.length) {
    return (
      <div className={`${dimensions} flex shrink-0 items-center justify-center rounded-lg border border-slate-700 bg-slate-900 text-xs font-black text-slate-300`}>
        {player?.carNumber ? `#${String(player.carNumber).replace(/^#/, "")}` : "—"}
      </div>
    );
  }

  return (
    <div className={`${dimensions} flex shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-700 bg-white/95 p-1`}>
      <img
        src={candidates[index]}
        alt={player?.carNumber ? `Car ${player.carNumber}` : player?.name}
        className="max-h-full max-w-full object-contain"
        onError={() => setIndex((current) => current + 1)}
      />
    </div>
  );
}

function Metric({ label, value, accent = false }) {
  return (
    <div className="min-w-0">
      <div className="text-[9px] font-bold uppercase tracking-[0.12em] text-slate-500">{label}</div>
      <div className={`truncate text-xs font-extrabold ${accent ? "text-sky-300" : "text-slate-200"}`}>{value}</div>
    </div>
  );
}

function EmptySlot({ slot, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex min-h-[82px] w-full items-center gap-3 rounded-xl border border-dashed px-3 py-3 text-left transition ${
        selected
          ? "border-sky-400 bg-sky-500/10"
          : "border-slate-700 bg-slate-950/50 hover:border-slate-500 hover:bg-slate-900"
      }`}
    >
      <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-700 bg-slate-900 text-xs font-black text-slate-400">
        {slot.label}
      </div>
      <div>
        <div className="text-xs font-extrabold text-slate-300">Select a driver</div>
        <div className="mt-0.5 text-[10px] text-slate-500">Click a driver from the pool</div>
      </div>
    </button>
  );
}

function FilledSlot({ slot, player, seriesConfig, selected, onSelect, onRemove }) {
  return (
    <div
      className={`group relative flex min-h-[82px] w-full items-center gap-3 rounded-xl border px-3 py-3 transition ${
        selected ? "border-sky-400 bg-sky-500/10" : "border-slate-800 bg-slate-950/70 hover:border-slate-700"
      }`}
    >
      <button type="button" onClick={onSelect} className="absolute inset-0 rounded-xl" aria-label={`Select ${slot.label}`} />
      <div className="relative z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-sky-500 text-xs font-black text-slate-950">
        {slot.label}
      </div>
      <div className="relative z-10 pointer-events-none">
        <DriverNumber player={player} seriesConfig={seriesConfig} size="sm" />
      </div>
      <div className="relative z-10 min-w-0 flex-1 pointer-events-none">
        <div className="truncate text-sm font-black text-white">{player.name}</div>
        <div className="mt-0.5 truncate text-[10px] font-semibold text-slate-400">
          {player.team || "Team unavailable"}
        </div>
        <div className="mt-1 flex items-center gap-3 text-[10px] text-slate-500">
          <span>{fmtMoney(player.salary)}</span>
          <span>{fmt1(player.proj)} pts</span>
        </div>
      </div>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onRemove();
        }}
        className="relative z-20 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-slate-700 bg-slate-900 text-sm font-black text-slate-400 opacity-70 transition hover:border-rose-500/60 hover:bg-rose-500/10 hover:text-rose-300 group-hover:opacity-100"
        aria-label={`Remove ${player.name}`}
      >
        ×
      </button>
    </div>
  );
}


function raceParseNumericLike(value) {
  if (value == null) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const parsed = Number(raw.replace(/[%,$\s]/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
}
function raceNorm(value) {
  return String(value ?? "").trim();
}
function raceDriverKey(name, stripSuffix = false) {
  let value = String(name ?? "").toLowerCase().trim();
  value = value.replace(/[.,'’\-]/g, " ").replace(/\s+/g, " ").trim();
  if (stripSuffix) value = value.replace(/\s+(jr|sr|ii|iii|iv|v)\s*$/i, "").trim();
  return value;
}
function raceDriverNameMatches(a, b) {
  const fullA = raceDriverKey(a, false);
  const fullB = raceDriverKey(b, false);
  if (fullA && fullB && fullA === fullB) return true;
  const shortA = raceDriverKey(a, true);
  const shortB = raceDriverKey(b, true);
  return Boolean(shortA && shortB && shortA === shortB);
}

/* ----------------------------- Race Log Modal ----------------------------- */
function raceFmt1(v) {
  const n = raceToNum(v);
  const r = Math.round(n * 10) / 10;
  return r;
}
function raceToNum(v) {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
}
function raceDkFinishPts(finish) {
  const f = Math.round(raceToNum(finish));
  if (!f || f < 1) return 0;
  if (f === 1) return 45;
  if (f === 2) return 42;
  if (f === 3) return 41;
  if (f === 4) return 40;
  if (f === 5) return 39;
  if (f === 6) return 38;
  if (f === 7) return 37;
  if (f === 8) return 36;
  if (f === 9) return 35;
  if (f === 10) return 34;
  if (f === 11) return 32;
  if (f === 12) return 31;
  if (f === 13) return 30;
  if (f === 14) return 29;
  if (f === 15) return 28;
  if (f === 16) return 27;
  if (f === 17) return 26;
  if (f === 18) return 25;
  if (f === 19) return 24;
  if (f === 20) return 23;
  if (f === 21) return 21;
  if (f === 22) return 20;
  if (f === 23) return 19;
  if (f === 24) return 18;
  if (f === 25) return 17;
  if (f === 26) return 16;
  if (f === 27) return 15;
  if (f === 28) return 14;
  if (f === 29) return 13;
  if (f === 30) return 12;
  if (f === 31) return 10;
  if (f === 32) return 9;
  if (f === 33) return 8;
  if (f === 34) return 7;
  if (f === 35) return 6;
  if (f === 36) return 5;
  if (f === 37) return 4;
  if (f === 38) return 3;
  if (f === 39) return 2;
  if (f === 40) return 1;
  return 0;
}
function raceFdFinishPts(finish) {
  const f = Math.round(raceToNum(finish));
  if (!f || f < 1) return 0;
  if (f === 1) return 43;
  if (f === 2) return 40;
  if (f === 3) return 38;
  if (f === 4) return 37;
  if (f === 5) return 36;
  if (f === 6) return 35;
  if (f === 7) return 34;
  if (f === 8) return 33;
  if (f === 9) return 32;
  if (f === 10) return 31;
  if (f === 11) return 30;
  if (f === 12) return 29;
  if (f === 13) return 28;
  if (f === 14) return 27;
  if (f === 15) return 26;
  if (f === 16) return 25;
  if (f === 17) return 24;
  if (f === 18) return 23;
  if (f === 19) return 22;
  if (f === 20) return 21;
  if (f === 21) return 20;
  if (f === 22) return 19;
  if (f === 23) return 18;
  if (f === 24) return 17;
  if (f === 25) return 16;
  if (f === 26) return 15;
  if (f === 27) return 14;
  if (f === 28) return 13;
  if (f === 29) return 12;
  if (f === 30) return 11;
  if (f === 31) return 10;
  if (f === 32) return 9;
  if (f === 33) return 8;
  if (f === 34) return 7;
  if (f === 35) return 6;
  if (f === 36) return 5;
  if (f === 37) return 4;
  if (f === 38) return 3;
  if (f === 39) return 2;
  if (f === 40) return 1;
  return 0;
}
function raceCalcDkPts({ finish, start, fastest_lap, laps_led }) {
  const fin = raceToNum(finish);
  const st = raceToNum(start);
  const pd = st && fin ? st - fin : 0;
  return raceDkFinishPts(fin) + pd * 1 + raceToNum(fastest_lap) * 0.45 + raceToNum(laps_led) * 0.25;
}
function raceCalcFdPts({ finish, start, laps, laps_led }) {
  const fin = raceToNum(finish);
  const st = raceToNum(start);
  const pd = st && fin ? st - fin : 0;
  return raceFdFinishPts(fin) + pd * 0.5 + raceToNum(laps) * 0.1 + raceToNum(laps_led) * 0.1;
}

function isDriverColumnName(colName) {
  const k = String(colName || "").trim().toLowerCase();
  return k === "driver" || k === "name" || k === "driver name";
}

function seriesLabelFromKey(key) {
  const k = String(key || "").toLowerCase();
  if (k === "xfinity") return "O'Reilly";
  if (k === "trucks") return "Trucks";
  return "Cup";
}

function safeYearFromDate(iso) {
  const s = String(iso || "");
  const y = s.slice(0, 4);
  return /^\d{4}$/.test(y) ? y : "";
}

function shortMD(iso) {
  const s = String(iso || "");
  if (s.length >= 10) return `${s.slice(5, 7)}/${s.slice(8, 10)}`;
  return s;
}

function na(v) {
  const raw = raceNorm(v);
  return raw ? raw : "N/A";
}

function normalizeRaceRow(r) {
  const start = raceParseNumericLike(r?.start);
  const finish = raceParseNumericLike(r?.finish);
  const avg = raceParseNumericLike(r?.avg);
  const ll = raceParseNumericLike(r?.laps_led);
  const fl = raceParseNumericLike(r?.fastest_lap);
  const laps = raceParseNumericLike(r?.laps);

  const dkPtsRaw = raceParseNumericLike(
    r?.dk_pts ?? r?.dk ?? r?.dk_points ?? r?.dkPts ?? r?.dk_points_total
  );
  const fdPtsRaw = raceParseNumericLike(
    r?.fd_pts ?? r?.fd ?? r?.fd_points ?? r?.fdPts ?? r?.fd_points_total
  );

  const dk =
    dkPtsRaw != null && dkPtsRaw !== ""
      ? dkPtsRaw
      : raceCalcDkPts({ finish, start, fastest_lap: fl, laps_led: ll });
  const fd =
    fdPtsRaw != null && fdPtsRaw !== ""
      ? fdPtsRaw
      : raceCalcFdPts({ finish, start, laps, laps_led: ll });

  return {
    date: r?.race_date || "",
    track: r?.track_name || "",
    start,
    finish,
    avgRun: avg,
    rating: raceParseNumericLike(r?.rating),
    lapsLed: ll,
    fastLaps: fl,
    laps,
    dk,
    fd,
  };
}

function DriverRaceLogModal({ open, onClose, driverName, seriesKey, raceLogs }) {
  const YEARS = [2026, 2025, 2024, 2023, 2022, 2021, 2020];
  const seriesLabel = useMemo(() => seriesLabelFromKey(seriesKey), [seriesKey]);

  const driverRows = useMemo(() => {
    if (!open) return [];
    const dn = String(driverName || "").trim();
    if (!dn) return [];
    return (raceLogs || []).filter(
      (r) => String(r?.series || "") === seriesLabel && raceDriverNameMatches(r?.driver_name, dn)
    );
  }, [open, driverName, raceLogs, seriesLabel]);

  const yearsWithData = useMemo(() => {
    const set = new Set();
    for (const r of driverRows) {
      const y = safeYearFromDate(r?.race_date);
      if (y) set.add(Number(y));
    }
    return Array.from(set).sort((a, b) => b - a);
  }, [driverRows]);

  const defaultYear = yearsWithData.length ? yearsWithData[0] : YEARS[0];
  const [activeYear, setActiveYear] = useState(defaultYear);

  useEffect(() => {
    if (!open) return;
    setActiveYear(defaultYear);
  }, [open, defaultYear, driverName, seriesLabel]);

  const yearRows = useMemo(() => {
    const y = String(activeYear);
    const filtered = driverRows.filter((r) => safeYearFromDate(r?.race_date) === y);
    filtered.sort((a, b) =>
      String(a?.race_date || "").localeCompare(String(b?.race_date || ""))
    );
    return filtered.map(normalizeRaceRow);
  }, [driverRows, activeYear]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[200]">
      <button
        type="button"
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
        aria-label="Close race log"
      />

      <div className="absolute left-1/2 top-1/2 w-[min(1040px,94vw)] -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-slate-800 bg-slate-950 shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-slate-800 bg-slate-950/90">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-[0.18em] text-sky-400 font-bold">
              Race Logs • {seriesLabel}
            </div>
            <div className="text-xl md:text-2xl font-extrabold text-slate-100 truncate">
              {driverName || "Driver"}
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 rounded-full border border-slate-700 bg-slate-900/70 text-slate-100 text-xs font-semibold hover:bg-slate-800 transition"
          >
            Close
          </button>
        </div>

        <div className="px-5 py-3 border-b border-slate-800 flex flex-wrap gap-2">
          {YEARS.map((y) => {
            const active = Number(activeYear) === Number(y);
            const disabled = yearsWithData.length && !yearsWithData.includes(Number(y));
            return (
              <button
                key={y}
                type="button"
                onClick={() => setActiveYear(y)}
                disabled={disabled}
                className={
                  "px-3 py-1.5 rounded-full text-[11px] font-semibold border transition " +
                  (active
                    ? "bg-sky-500 text-slate-950 border-sky-400"
                    : disabled
                    ? "bg-slate-950 text-slate-600 border-slate-800 cursor-not-allowed"
                    : "bg-slate-900/60 text-slate-200 border-slate-700 hover:bg-slate-800")
                }
              >
                {y}
              </button>
            );
          })}
        </div>

        <div className="max-h-[70vh] overflow-auto">
          {yearRows.length === 0 ? (
            <div className="px-5 py-6 text-sm text-slate-300">
              No race log data for <span className="font-semibold">{driverName}</span> in{" "}
              <span className="font-semibold">{seriesLabel}</span> for{" "}
              <span className="font-semibold">{activeYear}</span>.
            </div>
          ) : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 z-10 bg-slate-900">
                <tr className="text-slate-200">
                  <th className="px-4 py-2 text-left whitespace-nowrap">Date</th>
                  <th className="px-4 py-2 text-left whitespace-nowrap">Race</th>
                  <th className="px-3 py-2 text-center whitespace-nowrap">Start</th>
                  <th className="px-3 py-2 text-center whitespace-nowrap">Finish</th>
                  <th className="px-3 py-2 text-center whitespace-nowrap">Avg Run</th>
                  <th className="px-3 py-2 text-center whitespace-nowrap">Laps Led</th>
                  <th className="px-3 py-2 text-center whitespace-nowrap">Fast Laps</th>
                  <th className="px-3 py-2 text-center whitespace-nowrap">DK</th>
                  <th className="px-3 py-2 text-center whitespace-nowrap">FD</th>
                </tr>
              </thead>
              <tbody>
                {yearRows.map((rr, idx) => (
                  <tr
                    key={`${rr.date}-${rr.track}-${idx}`}
                    className={idx % 2 ? "bg-slate-900" : "bg-slate-950"}
                  >
                    <td className="px-4 py-2 text-left whitespace-nowrap text-slate-100">
                      {shortMD(rr.date)}
                    </td>
                    <td className="px-4 py-2 text-left whitespace-nowrap text-slate-100">
                      {na(rr.track)}
                    </td>
                    <td className="px-3 py-2 text-center text-slate-100">{na(rr.start)}</td>
                    <td className="px-3 py-2 text-center text-slate-100">{na(rr.finish)}</td>
                    <td className="px-3 py-2 text-center text-slate-100">
                      {rr.avgRun == null ? "n/a" : raceFmt1(rr.avgRun).toFixed(1)}
                    </td>
                    <td className="px-3 py-2 text-center text-slate-100">{na(rr.lapsLed)}</td>
                    <td className="px-3 py-2 text-center text-slate-100">{na(rr.fastLaps)}</td>
                    <td className="px-3 py-2 text-center text-slate-100">
                      {rr.dk == null ? "n/a" : raceFmt1(rr.dk).toFixed(1)}
                    </td>
                    <td className="px-3 py-2 text-center text-slate-100">
                      {rr.fd == null ? "n/a" : raceFmt1(rr.fd).toFixed(1)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-800 text-[11px] text-slate-400">
          Chronological order • Missing cells show N/A • If your race logs include dk_pts/fd_pts,
          those will show here automatically.
        </div>
      </div>
    </div>
  );
}



function useMetaUpdated(metaUrl) {
  const [result, setResult] = useState({ url: "", date: null });

  useEffect(() => {
    let alive = true;
    fetchJsonFresh(metaUrl)
      .then((meta) => {
        const iso = meta?.updated_iso || meta?.updated_utc;
        const epoch = meta?.updated_epoch;
        const date = iso
          ? new Date(iso)
          : Number.isFinite(epoch)
          ? new Date(epoch * 1000)
          : null;
        if (alive) setResult({ url: metaUrl, date: date && !isNaN(date) ? date : null });
      })
      .catch(() => {
        if (alive) setResult({ url: metaUrl, date: null });
      });
    return () => { alive = false; };
  }, [metaUrl]);

  return result.url === metaUrl ? result.date : null;
}

export default function NascarLineupLab() {
  const location = useLocation();
  const navigate = useNavigate();
  const routeSeries = useMemo(() => seriesFromPath(location.pathname), [location.pathname]);
  const [series, setSeries] = useState(routeSeries);

  useEffect(() => setSeries(routeSeries), [routeSeries]);

  const seriesConfig = SERIES_CONFIG[series] || SERIES_CONFIG.cup;
  const updatedAt = useMetaUpdated(seriesConfig.meta);
  const storagePrefix = `nascarLineupLab.${series}`;
  const [site, setSite] = useStickyState(`${storagePrefix}.site`, "dk");
  const [lineups, setLineups] = useStickyState(`${storagePrefix}.lineups`, { dk: {}, fd: {} });
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ key: "salary", direction: "desc" });
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [hideUnprojected, setHideUnprojected] = useState(true);
  const [rowsRaw, setRowsRaw] = useState([]);
  const [driverRowsRaw, setDriverRowsRaw] = useState([]);
  const [siteIdsRaw, setSiteIdsRaw] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [profile, setProfile] = useState(null);
  const [raceLogs, setRaceLogs] = useState([]);
  const poolScrollRef = useRef(null);

  const isBoth = site === "both";
  const activeSite = site === "fd" ? "fd" : "dk";
  const config = SITE_CONFIG[activeSite];
  const lineup = isBoth ? {} : lineups?.[activeSite] || {};
  const slots = useMemo(
    () => Array.from({ length: config.rosterSize }, (_, index) => ({ id: `D${index + 1}`, label: `D${index + 1}` })),
    [config.rosterSize]
  );

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    setRowsRaw([]);
    setDriverRowsRaw([]);
    setSiteIdsRaw({});

    Promise.all([
      fetchRows(seriesConfig.projections, { fresh: true }),
      fetchRows(seriesConfig.driverData, { fresh: true }).catch(() => []),
      fetchJsonFresh(seriesConfig.siteIds),
      fetchRows(NASCAR_RACE_LOGS_SOURCE, { fresh: true }).catch(() => []),
    ])
      .then(([projectionRows, driverRows, siteIds, raceLogRows]) => {
        if (!alive) return;
        setRowsRaw(projectionRows);
        setDriverRowsRaw(driverRows);
        setSiteIdsRaw(siteIds && typeof siteIds === "object" ? siteIds : {});
        setRaceLogs(Array.isArray(raceLogRows) ? raceLogRows : []);
      })
      .catch((loadError) => {
        if (!alive) return;
        setError(String(loadError));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [seriesConfig.projections, seriesConfig.driverData, seriesConfig.siteIds]);

  useEffect(() => {
    setSelectedSlot(null);
    setSearch("");
    setProfile(null);
  }, [series, site]);

  const updateLineup = (updater) => {
    if (isBoth) return;
    setLineups((current) => {
      const base = current && typeof current === "object" ? current : { dk: {}, fd: {} };
      const currentSite = base?.[activeSite] && typeof base[activeSite] === "object" ? base[activeSite] : {};
      const nextSite = typeof updater === "function" ? updater(currentSite) : updater;
      return { ...base, [activeSite]: nextSite };
    });
  };

  const players = useMemo(() => {
    const mergedRows = mergeDriverRows(Array.isArray(rowsRaw) ? rowsRaw : [], Array.isArray(driverRowsRaw) ? driverRowsRaw : []);
    const dkLookup = buildSiteLookup(siteIdsRaw, "dk");
    const fdLookup = buildSiteLookup(siteIdsRaw, "fd");

    return mergedRows
      .map((row, index) => {
        const name = getDriverName(row);
        if (!name) return null;
        const team = getTeamName(row) || getTeamName(row.__supplemental) || "";
        const carNumber = getCarNumber(row) || getCarNumber(row.__supplemental) || "";
        const make = getMake(row) || getMake(row.__supplemental) || "";

        const readSite = (siteKey, lookup) => {
          const cfg = SITE_CONFIG[siteKey];
          const record = lookup.get(normName(name)) || null;
          const recordSalary = getRecordSalary(record);
          const projectionSalary = numberLike(firstValue(row, cfg.salaryKeys, 0));
          const salary = recordSalary > 0 ? recordSalary : projectionSalary;
          const proj = numberLike(firstValue(row, cfg.projectionKeys, 0));
          const ownership = asRate(firstValue(row, cfg.ownershipKeys, 0));
          const optimal = asRate(firstValue(row, cfg.optimalKeys, 0));
          const floor = numberLike(firstValue(row, cfg.floorKeys, 0));
          const ceiling = numberLike(firstValue(row, cfg.ceilingKeys, 0));
          return {
            siteId: getRecordId(record, siteKey),
            salary,
            proj,
            ownership,
            optimal,
            floor,
            ceiling,
            value: salary > 0 ? proj / (salary / 1000) : 0,
            available: salary > 0,
          };
        };

        const dk = readSite("dk", dkLookup);
        const fd = readSite("fd", fdLookup);
        const active = activeSite === "fd" ? fd : dk;
        if (isBoth ? !dk.available && !fd.available : !active.available) return null;

        const start = numberLike(
  firstValue(
    row,
    [
      "Start",
      "Starting Position",
      "Start Pos",
      "Grid",
      "Qual",
      "Qualifying Position",
    ],
    0
  )
);
        const projFinish = numberLike(firstValue(row, ["Proj Fin", "Projected Finish", "Proj Finish", "Projection Finish"], 0));

        return {
          id: `${series}|${normName(name)}|${carNumber || index}`,
          name,
          team,
          carNumber,
          make,
          start,
          projFinish,
          dkSiteId: dk.siteId,
          fdSiteId: fd.siteId,
          dkSalary: dk.salary,
          fdSalary: fd.salary,
          dkProj: dk.proj,
          fdProj: fd.proj,
          dkOwnership: dk.ownership,
          fdOwnership: fd.ownership,
          dkOptimal: dk.optimal,
          fdOptimal: fd.optimal,
          dkFloor: dk.floor,
          fdFloor: fd.floor,
          dkCeiling: dk.ceiling,
          fdCeiling: fd.ceiling,
          dkValue: dk.value,
          fdValue: fd.value,
          salary: active.salary,
          proj: active.proj,
          ownership: active.ownership,
          optimal: active.optimal,
          floor: active.floor,
          ceiling: active.ceiling,
          value: active.value,
          siteId: active.siteId,
          hasProjection: dk.proj > 0 || fd.proj > 0,
          raw: row,
        };
      })
      .filter(Boolean);
  }, [rowsRaw, driverRowsRaw, siteIdsRaw, activeSite, isBoth, series]);

  const selectedPlayers = useMemo(() => Object.values(lineup).filter(Boolean), [lineup]);
  const selectedIds = useMemo(() => {
    const source = isBoth
      ? [...Object.values(lineups?.dk || {}), ...Object.values(lineups?.fd || {})]
      : selectedPlayers;
    return new Set(source.filter(Boolean).map((player) => player.id));
  }, [isBoth, lineups, selectedPlayers]);

  const totals = useMemo(() => {
    const salary = selectedPlayers.reduce((sum, player) => sum + numberLike(player.salary), 0);
    const projection = selectedPlayers.reduce((sum, player) => sum + numberLike(player.proj), 0);
    const ownership = selectedPlayers.reduce((sum, player) => sum + numberLike(player.ownership), 0);
    const ownershipProduct = selectedPlayers.reduce(
      (product, player) => product * Math.max(numberLike(player.ownership), 0.0001),
      1
    );
    const geometricOwnership = selectedPlayers.length
      ? Math.pow(ownershipProduct, 1 / selectedPlayers.length)
      : 0;
    const optimalProduct = selectedPlayers.reduce(
      (product, player) => product * Math.max(numberLike(player.optimal), 0.0001),
      1
    );
    const optimal = selectedPlayers.length
      ? Math.pow(optimalProduct, 1 / selectedPlayers.length)
      : 0;
    const floor = selectedPlayers.reduce((sum, player) => sum + numberLike(player.floor), 0);
    const ceiling = selectedPlayers.reduce((sum, player) => sum + numberLike(player.ceiling), 0);
    const remaining = config.cap - salary;
    const openSlots = config.rosterSize - selectedPlayers.length;
    const averageLeft = openSlots > 0 ? remaining / openSlots : 0;
    const complete = openSlots === 0;
    const valid = complete && remaining >= 0;
    return {
      salary,
      projection,
      ownership,
      geometricOwnership,
      optimal,
      floor,
      ceiling,
      remaining,
      openSlots,
      averageLeft,
      complete,
      valid,
    };
  }, [selectedPlayers, config]);

  const lastOpenSlot = useMemo(() => {
    if (isBoth || totals.openSlots !== 1) return null;
    return slots.find((slot) => !lineup[slot.id]) || null;
  }, [isBoth, totals.openSlots, slots, lineup]);
  const maxAffordableSalary = lastOpenSlot ? Math.max(0, totals.remaining) : null;

  const filteredPlayers = useMemo(() => {
    const query = search.trim().toLowerCase();
    const source = players.filter((player) => {
      if (hideUnprojected && !player.hasProjection) return false;
      if (query && !`${player.name} ${player.team} ${player.carNumber} ${player.make}`.toLowerCase().includes(query)) return false;
      return true;
    });
    const direction = sort.direction === "asc" ? 1 : -1;
    return source.sort((a, b) => {
      if (["name", "team"].includes(sort.key)) {
        return String(a[sort.key] || "").localeCompare(String(b[sort.key] || "")) * direction;
      }
      return (numberLike(a[sort.key]) - numberLike(b[sort.key])) * direction;
    });
  }, [players, search, hideUnprojected, sort]);

  useEffect(() => {
    if (!lastOpenSlot) return;
    setSelectedSlot(lastOpenSlot.id);
    setSort({ key: "salary", direction: "desc" });
  }, [lastOpenSlot]);

  useEffect(() => {
    if (!lastOpenSlot || !poolScrollRef.current) return;
    const frame = window.requestAnimationFrame(() => {
      const rows = Array.from(poolScrollRef.current?.querySelectorAll("[data-player-salary]") || []);
      const target = rows.find((row) => Number(row.dataset.playerSalary) <= maxAffordableSalary);
      if (target) target.scrollIntoView({ block: "start", behavior: "smooth" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [lastOpenSlot, maxAffordableSalary, filteredPlayers]);

  function nextOpenSlot(currentLineup = lineup) {
    return slots.find((slot) => !currentLineup?.[slot.id])?.id || null;
  }

  function sameDriver(a, b) {
    if (!a || !b) return false;
    if (a.id && b.id && a.id === b.id) return true;
    if (a.siteId && b.siteId && String(a.siteId) === String(b.siteId)) return true;
    return normName(a.name) === normName(b.name) && String(a.carNumber || "") === String(b.carNumber || "");
  }

  function toggleDriver(player) {
    if (isBoth) return;
    updateLineup((current) => {
      const next = { ...current };
      const matching = Object.entries(next)
        .filter(([, selectedPlayer]) => sameDriver(selectedPlayer, player))
        .map(([slotId]) => slotId);
      if (matching.length) {
        matching.forEach((slotId) => delete next[slotId]);
        return next;
      }
      if (lastOpenSlot && player.salary > maxAffordableSalary) return current;
      const target = selectedSlot && !next[selectedSlot] ? selectedSlot : nextOpenSlot(next);
      return target ? { ...next, [target]: player } : current;
    });
    setSelectedSlot(null);
  }

  function removeDriver(slotId) {
    updateLineup((current) => {
      const next = { ...current };
      delete next[slotId];
      return next;
    });
    setSelectedSlot(slotId);
  }

  function clearLineup() {
    updateLineup({});
    setSelectedSlot(null);
  }

  function exportLineup() {
    if (isBoth || !selectedPlayers.length) return;
    const orderedPlayers = slots.map((slot) => lineup[slot.id]).filter(Boolean);
    const header = ["Slot", "Driver", "Team", "Car", "Salary", "Projection", "Site ID"];
    const rows = orderedPlayers.map((player, index) => [
      slots[index]?.label || `D${index + 1}`,
      player.name,
      player.team,
      player.carNumber,
      player.salary,
      player.proj,
      player.siteId || "",
    ]);
    const csv = [header, ...rows].map((row) => row.map(escapeCSV).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${series}_${activeSite}_lineup.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  function toggleSort(key) {
    setSort((current) => ({
      key,
      direction: current.key === key && current.direction === "desc" ? "asc" : "desc",
    }));
  }

  const validationMessage = totals.valid
    ? `Valid ${config.label} lineup`
    : totals.remaining < 0
    ? `${fmtMoney(Math.abs(totals.remaining))} over the salary cap`
    : `${totals.openSlots} roster spot${totals.openSlots === 1 ? "" : "s"} remaining`;

  return (
    <div className="min-h-screen bg-slate-950 px-3 py-2 text-slate-100 md:px-4">
      <div className="mx-auto max-w-[1700px]">
        <header className="mb-2 flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="mb-0.5 text-[10px] font-bold uppercase tracking-[0.22em] text-sky-400">{seriesConfig.title}</div>
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <h1 className="text-xl font-black tracking-tight md:text-2xl">Lineup Lab</h1>
              {updatedAt && (
                <span className="text-xs font-normal italic text-slate-100">
                  Updated: {updatedAt.toLocaleString(undefined, { month: "numeric", day: "numeric", hour: "numeric", minute: "2-digit" })}
                </span>
              )}
            </div>
            <p className="mt-0.5 text-xs text-slate-400">Build by hand, inspect every driver, and validate the lineup in real time.</p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex rounded-xl border border-slate-800 bg-slate-900 p-1">
              {Object.values(SERIES_CONFIG).map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => navigate(option.route)}
                  className={`h-9 rounded-lg px-3 text-xs font-bold transition ${
                    series === option.key ? "bg-sky-500 text-slate-950" : "text-slate-400 hover:bg-slate-800 hover:text-white"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>

            <div className="inline-flex rounded-xl border border-slate-800 bg-slate-900 p-1">
              {["dk", "fd", "both"].map((siteKey) => (
                <button
                  key={siteKey}
                  type="button"
                  onClick={() => setSite(siteKey)}
                  className={`flex h-9 min-w-[58px] items-center justify-center gap-1.5 rounded-lg px-3 text-sm font-bold transition ${
                    site === siteKey ? "bg-slate-800 text-white ring-1 ring-sky-400" : "text-slate-400 hover:bg-slate-800 hover:text-white"
                  }`}
                >
                  {siteKey === "both" ? (
                    <>
                      <img src={SITE_CONFIG.dk.logo} alt="DraftKings" className="h-5 max-w-[34px] object-contain" />
                      <span className="text-[10px] font-black">+</span>
                      <img src={SITE_CONFIG.fd.logo} alt="FanDuel" className="h-5 max-w-[30px] object-contain" />
                    </>
                  ) : (
                    <img src={SITE_CONFIG[siteKey].logo} alt={SITE_CONFIG[siteKey].label} className="h-6 max-w-[46px] object-contain" />
                  )}
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={() => {
                if (isBoth) setLineups({ dk: {}, fd: {} });
                else clearLineup();
              }}
              className="h-9 rounded-xl border border-slate-700 bg-slate-900 px-3 text-xs font-semibold text-slate-300 hover:border-slate-600 hover:text-white"
            >
              {isBoth ? "Clear Both" : `Clear ${config.short}`}
            </button>
            {!isBoth ? (
              <button
                type="button"
                onClick={exportLineup}
                disabled={!selectedPlayers.length}
                className="h-9 rounded-xl border border-sky-500/40 bg-sky-500/10 px-3 text-xs font-semibold text-sky-300 hover:bg-sky-500/20 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Export CSV
              </button>
            ) : null}
          </div>
        </header>

        {error ? (
          <div className="mb-2 rounded-xl border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-sm font-semibold text-rose-200">
            Could not load NASCAR Lineup Lab data: {error}
          </div>
        ) : null}

        <div className="grid gap-2 xl:grid-cols-[minmax(0,1fr)_400px]">
          <section className="min-w-0 overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/70 shadow-2xl shadow-black/20">
            <div className="border-b border-slate-800 p-2.5">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="relative flex-1">
                  <input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search driver, team, car, or make..."
                    className="h-9 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 pr-9 text-xs text-white outline-none placeholder:text-slate-600 focus:border-sky-500"
                  />
                  {search ? (
                    <button type="button" onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white">×</button>
                  ) : null}
                </div>
                <select
                  value={sort.key}
                  onChange={(event) => setSort((current) => ({ ...current, key: event.target.value }))}
                  className="h-9 rounded-lg border border-slate-700 bg-slate-950 px-3 text-xs font-bold text-slate-300 outline-none focus:border-sky-500"
                >
                  {SORT_OPTIONS.map((option) => <option key={option.key} value={option.key}>{option.label}</option>)}
                </select>
                <button
                  type="button"
                  onClick={() => setSort((current) => ({ ...current, direction: current.direction === "desc" ? "asc" : "desc" }))}
                  className="h-9 rounded-lg border border-slate-700 bg-slate-950 px-3 text-xs font-bold text-slate-300 hover:text-white"
                >
                  {sort.direction === "desc" ? "High → Low" : "Low → High"}
                </button>
                <label className="flex items-center gap-2 text-xs font-medium text-slate-400">
                  <input
                    type="checkbox"
                    checked={hideUnprojected}
                    onChange={(event) => setHideUnprojected(event.target.checked)}
                    className="h-4 w-4 accent-sky-500"
                  />
                  Hide unprojected
                </label>
              </div>
            </div>

            {!isBoth && lastOpenSlot ? (
              <div className="flex items-center justify-between gap-3 border-b border-amber-400/20 bg-amber-400/10 px-3 py-2 text-xs">
                <div><span className="font-bold text-amber-300">Finish lineup:</span><span className="ml-2 text-slate-300">Add a driver for {fmtMoney(maxAffordableSalary)} or less</span></div>
                <span className="shrink-0 rounded-full border border-amber-400/30 bg-slate-950/40 px-2 py-1 font-bold text-amber-200">Max {fmtMoney(maxAffordableSalary)}</span>
              </div>
            ) : null}

            <div ref={poolScrollRef} className="h-[calc(100vh-205px)] min-h-[420px] max-h-[700px] overflow-auto">
              <table className={`w-max min-w-full table-auto border-collapse text-xs ${isBoth ? "min-w-[780px]" : "min-w-[980px]"}`}>
                <thead className="sticky top-0 z-10 bg-slate-950/95 backdrop-blur">
                  <tr className="border-b border-slate-800 text-[10px] uppercase tracking-[0.08em] text-slate-500">
                    <th className="sticky left-0 z-30 whitespace-nowrap bg-slate-950 px-3 py-2 text-left shadow-[8px_0_12px_-10px_rgba(0,0,0,0.95)]">Driver</th>
                    {(isBoth
                      ? [["dkSalary", "DK Sal"], ["fdSalary", "FD Sal"], ["dkProj", "DK Proj"], ["fdProj", "FD Proj"]]
                      : [["salary", "Salary"], ["proj", "Proj"], ["value", "Value"], ["ownership", "Own%"], ["optimal", "Opt%"], ["ceiling", "Ceil"]]
                    ).map(([key, label]) => (
                      <th key={key} className="px-2 py-2 text-right">
                        <button type="button" onClick={() => toggleSort(key)} className="hover:text-white">
                          {label} {sort.key === key ? (sort.direction === "desc" ? "▼" : "▲") : ""}
                        </button>
                      </th>
                    ))}
                    <th className="px-2 py-2 text-right"><button type="button" onClick={() => toggleSort("start")} className="hover:text-white">Start</button></th>
                    <th className="px-2 py-2 text-right"><button type="button" onClick={() => toggleSort("projFinish")} className="hover:text-white">P. Fin</button></th>
                    {!isBoth ? <th className="w-14 px-2 py-2 text-center">Add</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={isBoth ? 7 : 10} className="px-4 py-16 text-center text-slate-500">Loading driver pool…</td></tr>
                  ) : filteredPlayers.length === 0 ? (
                    <tr><td colSpan={isBoth ? 7 : 10} className="px-4 py-16 text-center text-slate-500">No drivers match these filters.</td></tr>
                  ) : (
                    filteredPlayers.map((player, index) => {
                      const selected = selectedIds.has(player.id);
                      const unaffordable = !isBoth && Boolean(lastOpenSlot) && player.salary > maxAffordableSalary && !selected;
                      const full = !isBoth && totals.openSlots === 0 && !selected;
                      const canAdd = !isBoth && !unaffordable && !full;
                      return (
                        <tr
                          key={player.id}
                          data-player-salary={isBoth ? Math.max(player.dkSalary || 0, player.fdSalary || 0) : player.salary}
                          className={`border-b border-slate-800/70 transition ${
                            unaffordable ? "bg-rose-950/25 opacity-55" : selected ? "bg-sky-500/5" : index % 2 ? "bg-slate-950/20" : "bg-transparent"
                          } ${unaffordable ? "hover:bg-rose-950/30" : "hover:bg-slate-800/50"}`}
                        >
                          <td className="sticky left-0 z-[5] whitespace-nowrap bg-slate-950 px-3 py-1 shadow-[8px_0_12px_-10px_rgba(0,0,0,0.95)]">
                            <div className="flex items-center gap-2">
                              <DriverNumber player={player} seriesConfig={seriesConfig} size="sm" />
                              <div className="min-w-0">
                                <button type="button" onClick={() => setProfile(player)} className="block max-w-[240px] truncate text-left font-bold text-slate-100 underline decoration-slate-600 underline-offset-4 hover:text-sky-300">{player.name}</button>
                                <div className="mt-0.5 max-w-[260px] truncate text-[10px] text-slate-500">{player.team || "Team unavailable"}{player.make ? ` • ${player.make}` : ""}{player.carNumber ? ` • #${String(player.carNumber).replace(/^#/, "")}` : ""}</div>
                              </div>
                            </div>
                          </td>
                          {isBoth ? (
                            <>
                              <td className="px-2 py-1 text-right font-semibold tabular-nums text-slate-100">{player.dkSalary > 0 ? fmtMoney(player.dkSalary) : "—"}</td>
                              <td className="px-2 py-1 text-right font-semibold tabular-nums text-slate-100">{player.fdSalary > 0 ? fmtMoney(player.fdSalary) : "—"}</td>
                              <td className="px-2 py-1 text-right font-bold tabular-nums text-sky-300">{player.dkSalary > 0 ? fmt1(player.dkProj) : "—"}</td>
                              <td className="px-2 py-1 text-right font-bold tabular-nums text-sky-300">{player.fdSalary > 0 ? fmt1(player.fdProj) : "—"}</td>
                            </>
                          ) : (
                            <>
                              <td className={`px-2 py-1 text-right font-semibold tabular-nums ${unaffordable ? "text-rose-400 line-through" : "text-slate-100"}`}>{fmtMoney(player.salary)}</td>
                              <td className="px-2 py-1 text-right font-bold tabular-nums text-sky-300">{fmt1(player.proj)}</td>
                              <td className="px-2 py-1 text-right tabular-nums text-lime-300">{fmt1(player.value)}</td>
                              <td className="px-2 py-1 text-right tabular-nums text-amber-300">{fmtPct(player.ownership)}</td>
                              <td className="px-2 py-1 text-right font-semibold tabular-nums text-emerald-300">{fmtPct(player.optimal)}</td>
                              <td className="px-2 py-1 text-right tabular-nums text-slate-300">{fmt1(player.ceiling)}</td>
                            </>
                          )}
                          <td className="px-2 py-1 text-right tabular-nums text-slate-300">{player.start > 0 ? player.start : "—"}</td>
                          <td className="px-2 py-1 text-right tabular-nums text-slate-300">{player.projFinish > 0 ? fmt1(player.projFinish) : "—"}</td>
                          {!isBoth ? (
                            <td className="px-2 py-1 text-center">
                              <button
                                type="button"
                                disabled={!selected && !canAdd}
                                onClick={(event) => { event.stopPropagation(); toggleDriver(player); }}
                                title={selected ? "Remove driver" : unaffordable ? `Too expensive — max ${fmtMoney(maxAffordableSalary)}` : full ? "Lineup is full" : "Add driver"}
                                className={`inline-flex h-7 w-7 items-center justify-center rounded-lg border text-lg font-bold transition ${
                                  selected ? "border-sky-500/30 bg-sky-500/10 text-sky-400" : canAdd ? "border-sky-500 bg-sky-500 text-slate-950 hover:bg-sky-400" : unaffordable ? "cursor-not-allowed border-rose-500/30 bg-rose-950/30 text-rose-500/60" : "cursor-not-allowed border-slate-800 bg-slate-900 text-slate-700"
                                }`}
                              >
                                {selected ? "✓" : "+"}
                              </button>
                            </td>
                          ) : null}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <aside className="xl:sticky xl:top-[60px] xl:self-start">
            {isBoth ? (
              <div className="space-y-2">
                {["dk", "fd"].map((siteKey) => {
                  const cfg = SITE_CONFIG[siteKey];
                  const savedLineup = lineups?.[siteKey] || {};
                  const savedPlayers = Object.values(savedLineup).filter(Boolean);
                  const salary = savedPlayers.reduce((sum, player) => sum + numberLike(player.salary), 0);
                  const projection = savedPlayers.reduce((sum, player) => sum + numberLike(player.proj), 0);
                  const remaining = cfg.cap - salary;
                  return (
                    <button key={siteKey} type="button" onClick={() => setSite(siteKey)} className="w-full overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/95 text-left shadow-2xl shadow-black/30 transition hover:border-sky-500/60">
                      <div className="flex items-center justify-between border-b border-slate-800 p-3">
                        <div className="flex items-center gap-2">
                          <img src={cfg.logo} alt={cfg.label} className="h-6 max-w-[55px] object-contain" />
                          <div><div className="text-[9px] font-bold uppercase tracking-[0.18em] text-sky-400">{cfg.label}</div><div className="text-sm font-black text-white">Current Lineup</div></div>
                        </div>
                        <div className="rounded-full border border-slate-700 bg-slate-950 px-3 py-1 text-xs font-bold text-slate-300">{savedPlayers.length} / {cfg.rosterSize}</div>
                      </div>
                      <div className="grid grid-cols-3 gap-1 p-3">
                        <div className="rounded-lg border border-slate-800 bg-slate-950/70 px-2 py-1"><Metric label="Salary" value={fmtMoney(salary)} /></div>
                        <div className={`rounded-lg border px-2 py-1 ${remaining < 0 ? "border-rose-500/30 bg-rose-500/10" : "border-slate-800 bg-slate-950/70"}`}><Metric label="Remaining" value={fmtMoney(remaining)} /></div>
                        <div className="rounded-lg border border-slate-800 bg-slate-950/70 px-2 py-1"><Metric label="Projection" value={fmt1(projection)} accent /></div>
                      </div>
                      <div className="px-3 pb-3 text-[10px] text-slate-500">Click to edit this lineup.</div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className={`overflow-hidden rounded-2xl border bg-slate-900/95 shadow-2xl shadow-black/30 transition ${totals.valid ? "border-emerald-500/60" : totals.remaining < 0 ? "border-rose-500/60" : "border-slate-800"}`}>
                <div className="border-b border-slate-800 p-2.5">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2"><img src={config.logo} alt={config.label} className="h-5 max-w-[52px] object-contain" /><span className="text-[9px] font-bold uppercase tracking-[0.18em] text-sky-400">{config.label}</span></div>
                      <h2 className="text-base font-black">Your Lineup</h2>
                    </div>
                    <div className="rounded-full border border-slate-700 bg-slate-950 px-3 py-1 text-xs font-bold text-slate-300">{selectedPlayers.length} / {config.rosterSize}</div>
                  </div>

                  <div className="mt-1.5 grid grid-cols-3 gap-1">
                    <div className="rounded-lg border border-slate-800 bg-slate-950/70 px-2 py-1"><Metric label="Salary" value={fmtMoney(totals.salary)} /></div>
                    <div className={`rounded-lg border px-2 py-1 ${totals.remaining < 0 ? "border-rose-500/30 bg-rose-500/10" : "border-slate-800 bg-slate-950/70"}`}><Metric label="Remaining" value={fmtMoney(totals.remaining)} /></div>
                    <div className="rounded-lg border border-slate-800 bg-slate-950/70 px-2 py-1"><Metric label="Avg. Left" value={totals.openSlots ? fmtMoney(totals.averageLeft) : "—"} /></div>
                  </div>
                  <div className="mt-1 grid grid-cols-3 gap-1">
                    <div className="rounded-lg border border-slate-800 bg-slate-950/70 px-2 py-1"><Metric label="Projection" value={fmt1(totals.projection)} accent /></div>
                    <div className="rounded-lg border border-slate-800 bg-slate-950/70 px-2 py-1"><Metric label="Ownership" value={fmtPct(totals.ownership)} /></div>
                    <div className="rounded-lg border border-slate-800 bg-slate-950/70 px-2 py-1"><Metric label="Lineup Opt%" value={fmtPct(totals.optimal)} /></div>
                  </div>
                </div>

                <div className="divide-y divide-slate-800">
                  {slots.map((slot) => {
                    const player = lineup[slot.id];
                    const active = selectedSlot === slot.id;
                    return (
                      <div key={slot.id} className={`grid min-h-[46px] grid-cols-[38px_minmax(0,1fr)_64px_26px] items-center gap-1.5 px-2.5 py-1 transition ${active ? "bg-sky-500/10" : "hover:bg-slate-800/40"}`}>
                        <button type="button" onClick={() => !player && setSelectedSlot(active ? null : slot.id)} className={`rounded-md border px-1.5 py-1.5 text-[10px] font-black ${active ? "border-sky-400 bg-sky-500 text-slate-950" : "border-slate-700 bg-slate-950 text-slate-400"}`}>{slot.label}</button>
                        {player ? (
                          <button type="button" onClick={() => setProfile(player)} className="min-w-0 text-left">
                            <div className="truncate text-xs font-bold text-white hover:text-sky-300">{player.name}</div>
                            <div className="mt-0.5 flex items-center gap-1.5 text-[10px] text-slate-500"><span>#{String(player.carNumber || "—").replace(/^#/, "")}</span><span>•</span><span>{fmt1(player.proj)} pts</span></div>
                          </button>
                        ) : (
                          <button type="button" onClick={() => setSelectedSlot(active ? null : slot.id)} className="text-left text-xs font-medium text-slate-600 hover:text-slate-300">Add driver</button>
                        )}
                        <div className="text-right">
                          {player ? <><div className="text-xs font-bold tabular-nums text-slate-200">{fmtMoney(player.salary)}</div><div className="text-[10px] tabular-nums text-emerald-400">{fmtPct(player.ownership)} pOWN</div></> : <span className="text-slate-700">—</span>}
                        </div>
                        <button type="button" disabled={!player} onClick={() => removeDriver(slot.id)} className={`h-7 w-7 rounded-md border text-base ${player ? "border-slate-700 text-slate-500 hover:border-rose-500/50 hover:bg-rose-500/10 hover:text-rose-400" : "cursor-default border-transparent text-transparent"}`}>×</button>
                      </div>
                    );
                  })}
                </div>

                <div className="border-t border-slate-800 p-3">
                  <div className={`flex items-center justify-between rounded-lg border px-3 py-2 ${totals.valid ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : totals.remaining < 0 ? "border-rose-500/40 bg-rose-500/10 text-rose-300" : "border-slate-700 bg-slate-950 text-slate-400"}`}>
                    <span className="text-xs font-bold">{validationMessage}</span><span className="text-lg">{totals.valid ? "✓" : totals.remaining < 0 ? "!" : "•"}</span>
                  </div>
                  <div className="mt-2 grid grid-cols-3 gap-2 text-[11px]">
                    <div className="rounded-xl border border-slate-800 bg-slate-950/70 px-3 py-2"><div className="text-slate-500">Floor</div><div className="mt-0.5 font-bold tabular-nums text-slate-200">{fmt1(totals.floor)}</div></div>
                    <div className="rounded-xl border border-slate-800 bg-slate-950/70 px-3 py-2"><div className="text-slate-500">Ceiling</div><div className="mt-0.5 font-bold tabular-nums text-slate-200">{fmt1(totals.ceiling)}</div></div>
                    <div className="rounded-xl border border-slate-800 bg-slate-950/70 px-3 py-2"><div className="text-slate-500">Geo Own</div><div className="mt-0.5 font-bold tabular-nums text-slate-200">{fmtPct(totals.geometricOwnership)}</div></div>
                  </div>
                </div>
              </div>
            )}
          </aside>
        </div>
      </div>

      <DriverRaceLogModal
        open={Boolean(profile)}
        onClose={() => setProfile(null)}
        driverName={profile?.name || ""}
        seriesKey={series}
        raceLogs={raceLogs}
      />
    </div>
  );
}
